/**
 * What is behind one headline figure.
 *
 * A tile that says "Bounced 1" and cannot be opened is a dead end: the only
 * useful next question is *which one*, and answering it meant hunting through
 * campaigns one at a time. Each figure is now the door to the rows it counted.
 *
 * Everything here is derived, never stored — the same sends, replies and
 * campaigns the tiles add up, listed instead of totalled. So the two can never
 * disagree.
 */
import { useMemo, useEffect } from 'react';
import { useApp } from '../../store/AppContext.jsx';
import Icon from '../../lib/icons.jsx';
import Sheet from '../Sheet.jsx';
import { Avatar, Pill, Dash, EmptyState } from '../Primitives.jsx';
import { download } from '../../lib/format.js';
import { channelLabel, channelIcon, channelPill } from '../../constants/marketing.js';
import { campaignRecipients, campaignStats, replyOwners } from '../../domain/marketing.js';

const META = {
  campaigns: ['Campaigns', 'mega', 'g-pink', 'Every campaign in this workspace.'],
  sent: ['Messages sent', 'send', 'g-blue', 'Every message the mail server accepted.'],
  delivery: ['Delivery', 'check', 'g-green',
    'Accepted against attempted. “Accepted” is the last thing an SMTP send knows for certain.'],
  replied: ['Replies', 'chat', 'g-purple', 'People who wrote back, read out of the watched mailbox.'],
  bounced: ['Bounces', 'alert', 'g-amber',
    'Addresses that refused delivery. The mail system says so in a message, and this is what it said.'],
  failed: ['Refused', 'x', 'g-red', 'Messages the mail server would not take, and why.'],
  opened: ['Read', 'eye', 'g-purple',
    'People whose mail client loaded the tracking pixel, plus everyone who clicked - a click '
    + 'is proof of a read whether the pictures ever loaded or not.'],
  clicked: ['Clicks', 'target', 'g-blue',
    'People who pressed something in a message. The firmest signal here: a picture can be '
    + 'loaded by a machine, but a link is pressed by a person.']
};

const STATUS_TONE = {
  Sent: 'p-green', Sending: 'p-amber', Scheduled: 'p-blue', Paused: 'p-red', Draft: 'p-grey'
};

const when = value => {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(+d) ? String(value) : d.toLocaleString();
};

export default function MarketingDetail({ metric, channel, onClose }) {
  const { mkt, mktCtx, replies, events, tracking, loadEvents, setSheet, toast } = useApp();

  const [title, icon, tone, blurb] = META[metric] || META.campaigns;

  /*
   * Opened from a channel's own screen, this is that channel's share of the
   * figure and nothing else. A tile on the SMS page reading "Sent 4" that
   * opens a list of four emails is worse than no list at all.
   */
  const campaigns = useMemo(
    () => (channel ? mkt.campaigns.filter(c => c.channel === channel) : mkt.campaigns),
    [mkt.campaigns, channel]
  );

  /* Opens and clicks are server-owned, and only asked for when a screen wants
     them. */
  useEffect(() => {
    if (metric === 'opened' || metric === 'clicked') loadEvents();
  }, [metric, loadEvents]);

  /**
   * Everyone every campaign was sent to, with the campaign beside them.
   *
   * Built once for the whole sheet: each metric is a different slice of the
   * same list, and resolving recipients per campaign inside a loop for every
   * one of them is the same work several times over.
   */
  const sends = useMemo(() => {
    const out = [];

    campaigns.forEach(c => {
      const people = new Map(campaignRecipients(c, mktCtx).map(r => [r.id, r]));

      (c.sends || []).forEach(s => {
        const who = people.get(s.rid) || {};
        out.push({
          key: `${c.id}:${s.rid}`,
          campaign: c,
          name: who.name || '(no longer on the list)',
          to: c.channel === 'email' ? who.email : who.phone,
          status: s.status,
          error: s.error || '',
          via: s.via || '',
          at: s.at || ''
        });
      });
    });

    return out;
  }, [campaigns, mktCtx]);

  /**
   * A reply or bounce, matched to the campaign it answers.
   *
   * The matching lives in the domain rather than here, so this sheet and the
   * campaign's own Responses tab can never disagree about whose reply it is.
   */
  const inbox = useMemo(() => {
    const owners = replyOwners(mktCtx);
    const byCampaign = new Map(campaigns.map(c => [c.id, c]));
    const byAddress = new Map(
      sends.filter(s => s.status === 'sent')
        .map(s => [String(s.to || '').trim().toLowerCase(), s.name]));

    return (replies || []).map(r => {
      const owner = owners.get(r.id);
      return {
        ...r,
        who: byAddress.get(String(r.from_email || '').trim().toLowerCase())
          || r.from_name || '',
        campaign: (owner && byCampaign.get(owner.campaignId)) || null
      };
    });
  }, [replies, sends, campaigns, mktCtx]);

  /**
   * Everyone who opened or clicked, with what they did and when.
   *
   * Only events with a send behind them. An open from somebody we have no
   * record of writing to cannot be attributed to anything, and counting those
   * anyway is what made the headline read rate come out above 100%.
   */
  const engaged = useMemo(() => {
    const byKey = new Map();
    sends.forEach(x => {
      if (x.status === 'sent') byKey.set(`${x.campaign.id}|${x.rid}`, x);
    });

    const seen = new Map();

    (events || []).forEach(e => {
      const key = `${e.campaign_id}|${e.rid}`;
      const send = byKey.get(key);
      if (!send) return;

      if (!seen.has(key)) {
        seen.set(key, {
          key,
          name: send.name,
          to: send.to,
          campaign: send.campaign,
          opens: 0,
          clicks: 0,
          links: new Set(),
          last: ''
        });
      }

      const row = seen.get(key);
      if (e.kind === 'open') row.opens += 1;
      if (e.kind === 'click') { row.clicks += 1; if (e.url) row.links.add(e.url); }
      if (!row.last || String(e.at) > String(row.last)) row.last = e.at;
    });

    return [...seen.values()].sort((a, b) => String(b.last).localeCompare(String(a.last)));
  }, [events, sends]);

  const accepted = sends.filter(s => s.status === 'sent');
  const refused = sends.filter(s => s.status === 'failed');
  const answered = inbox.filter(r => r.kind === 'reply' && r.campaign);
  const bounced = inbox.filter(r => r.kind === 'bounce');

  const open = c => { onClose(); setSheet({ kind: 'campaignView', id: c.id }); };

  const exportRows = (name, head, lines) => {
    const q = v => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`;
    const csv = [head.map(q).join(',')].concat(lines.map(r => r.map(q).join(',')));
    download(
      new Blob([`﻿${csv.join('\r\n')}`], { type: 'text/csv;charset=utf-8' }),
      `${name}.csv`
    );
    toast(`Exported ${lines.length} row${lines.length === 1 ? '' : 's'}`);
  };

  /** The one line of arithmetic each figure comes from. */
  const sum = metric === 'campaigns' ? `${campaigns.length} campaigns`
    : metric === 'sent' ? `${accepted.length} accepted`
      : metric === 'delivery'
        ? `${accepted.length} accepted of ${accepted.length + refused.length} attempted`
        : metric === 'replied' ? `${answered.length} replies`
          : metric === 'bounced' ? `${bounced.length} bounces`
            : metric === 'opened'
              ? `${engaged.length} of ${accepted.length} read`
              : metric === 'clicked'
                ? `${engaged.filter(r => r.clicks).length} of ${accepted.length} clicked`
                : `${refused.length} refused`;

  const campaignCell = c => (c ? (
    <button type="button" className="lnkbtn" onClick={() => open(c)}>{c.name}</button>
  ) : <span className="dim">not matched to a campaign</span>);

  return (
    <Sheet
      icon={icon}
      tone={tone}
      title={title}
      onClose={onClose}
      closeLabel="Close"
      bodyClass="cvbody"
      wide
    >
      <div className="cvwrap">
        <section className="cvpanel">
          <div className="cvlogbar" style={{ justifyContent: 'space-between' }}>
            <span className="cvchip on">{sum}</span>
          </div>
          <p className="cvnote" style={{ margin: '0 0 14px' }}>
            {blurb}
            {channel ? ` ${channelLabel(channel)} only.` : ''}
          </p>

          {/* ------------------------------------------------- campaigns */}
          {metric === 'campaigns' ? (
            <div className="tscroll">
              <table>
                <thead>
                  <tr><th>Campaign</th><th>Channel</th><th>Status</th><th>Reach</th><th>Sent</th><th>When</th></tr>
                </thead>
                <tbody>
                  {campaigns.map(c => {
                    const st = campaignStats(c, mktCtx);
                    return (
                      <tr key={c.id} onClick={() => open(c)}>
                        <td>
                          <div className="cname">
                            <Avatar name={c.name} />
                            <div><b>{c.name}</b><small>{c.subject || c.goal || ''}</small></div>
                          </div>
                        </td>
                        <td>
                          <Pill tone={channelPill(c.channel)} icon={channelIcon(c.channel)}>
                            {channelLabel(c.channel)}
                          </Pill>
                        </td>
                        <td><Pill tone={STATUS_TONE[c.status] || 'p-grey'}>{c.status}</Pill></td>
                        <td>{st.reach}</td>
                        <td>{st.sent}</td>
                        <td className="dim">{c.created || <Dash />}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : null}

          {/* --------------------------------- sent, delivery and refused */}
          {['sent', 'delivery', 'failed'].includes(metric) ? (() => {
            const list = metric === 'failed' ? refused
              : metric === 'sent' ? accepted
                : [...accepted, ...refused];

            if (!list.length) {
              return (
                <EmptyState icon="send" tone="g-slate" title="Nothing here yet">
                  Send a campaign and every message shows up here.
                </EmptyState>
              );
            }

            return (
              <>
                <div className="crow" style={{ justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    className="btn sm"
                    onClick={() => exportRows(metric, ['Name', 'Address', 'Campaign', 'Status', 'Through', 'When', 'Why not'],
                      list.map(s => [s.name, s.to, s.campaign.name, s.status, s.via, s.at, s.error]))}
                  >
                    <Icon name="down" />Export {list.length}
                  </button>
                </div>

                <div className="tscroll">
                  <table>
                    <thead>
                      <tr><th>Contact</th><th>Address</th><th>Campaign</th><th>Status</th><th>Through</th><th>When</th></tr>
                    </thead>
                    <tbody>
                      {list.slice(0, 500).map(s => (
                        <tr key={s.key}>
                          <td>
                            <div className="cname">
                              <Avatar name={s.name} />
                              <div><b>{s.name}</b></div>
                            </div>
                          </td>
                          <td className="dim">{s.to || <Dash />}</td>
                          <td>{campaignCell(s.campaign)}</td>
                          <td>
                            <Pill tone={s.status === 'sent' ? 'p-green' : 'p-red'}>{s.status}</Pill>
                            {s.error ? <div className="cverr">{s.error}</div> : null}
                          </td>
                          <td className="dim">{s.via || <Dash />}</td>
                          <td className="dim">{s.at || <Dash />}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {list.length > 500
                  ? <p className="cvnote">Showing the first 500 of {list.length} — export for the lot.</p>
                  : null}
              </>
            );
          })() : null}

          {/* ------------------------------------- replies and bounces */}
          {['replied', 'bounced'].includes(metric) ? (() => {
            const list = metric === 'bounced' ? bounced : answered;

            if (!list.length) {
              return (
                <EmptyState
                  icon={metric === 'bounced' ? 'alert' : 'chat'}
                  tone="g-slate"
                  title={metric === 'bounced' ? 'Nothing has bounced' : 'Nobody has written back'}
                >
                  {metric === 'bounced'
                    ? 'A dead address announces itself in a message from the mail system, and it would be listed here.'
                    : 'Replies show up here as the mailbox is read.'}
                </EmptyState>
              );
            }

            return (
              <div className="cvthread">
                {list.map(r => (
                  <article className={`cvmsg ${r.kind}`} key={r.id}>
                    <header>
                      <Avatar name={r.who || r.from_email} />
                      <div>
                        <b>{r.who || r.from_email}</b>
                        <small>{r.from_email}</small>
                      </div>
                      {r.campaign
                        ? <Pill tone="p-grey">{r.campaign.name}</Pill>
                        : <Pill tone="p-grey">no campaign</Pill>}
                      <time>{when(r.received_at)}</time>
                    </header>

                    {r.subject ? <b className="cvsub">{r.subject}</b> : null}
                    {r.snippet ? <p>{r.snippet}</p> : null}

                    {r.campaign ? (
                      <div className="obacts" style={{ marginTop: 10 }}>
                        <button type="button" className="btn sm" onClick={() => open(r.campaign)}>
                          Open the campaign
                        </button>
                      </div>
                    ) : null}
                  </article>
                ))}
              </div>
            );
          })() : null}

          {/* ------------------------------------------ reads and clicks */}
          {['opened', 'clicked'].includes(metric) ? (() => {
            if (!tracking) {
              return (
                <EmptyState icon="eye" tone="g-slate" title="Tracking is not switched on">
                  Knowing a message was opened means putting a picture in it that the reader&rsquo;s
                  mail app fetches from this server; knowing it was clicked means routing every
                  link through it. Both need PUBLIC_URL set to an address a stranger&rsquo;s mail
                  client can reach.
                </EmptyState>
              );
            }

            const list = metric === 'clicked' ? engaged.filter(r => r.clicks) : engaged;

            if (!list.length) {
              return (
                <EmptyState
                  icon={metric === 'clicked' ? 'target' : 'eye'}
                  tone="g-slate"
                  title={metric === 'clicked' ? 'Nobody has clicked yet' : 'Nobody has read one yet'}
                >
                  Tracking goes into a message as it is sent, so only campaigns sent since it was
                  switched on can report anything. It cannot be added to mail already gone.
                </EmptyState>
              );
            }

            return (
              <>
                <div className="crow" style={{ justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    className="btn sm"
                    onClick={() => exportRows(metric,
                      ['Name', 'Address', 'Campaign', 'Opens', 'Clicks', 'Links', 'Last seen'],
                      list.map(r => [r.name, r.to, r.campaign.name, r.opens, r.clicks,
                        [...r.links].join(' | '), when(r.last)]))}
                  >
                    <Icon name="down" />Export {list.length}
                  </button>
                </div>

                <div className="tscroll">
                  <table>
                    <thead>
                      <tr>
                        <th>Contact</th><th>Address</th><th>Campaign</th>
                        <th>Opens</th><th>Clicks</th><th>What they pressed</th><th>Last seen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {list.map(r => (
                        <tr key={r.key}>
                          <td>
                            <div className="cname">
                              <Avatar name={r.name} />
                              <div><b>{r.name}</b></div>
                            </div>
                          </td>
                          <td className="dim">{r.to || <Dash />}</td>
                          <td>{campaignCell(r.campaign)}</td>
                          <td>{r.opens ? <Pill tone="p-purple">{r.opens}</Pill> : <Dash />}</td>
                          <td>{r.clicks ? <Pill tone="p-blue">{r.clicks}</Pill> : <Dash />}</td>
                          <td className="dim">
                            {r.links.size
                              ? [...r.links].map(u => (
                                <div key={u}>{u.replace(/^https?:\/\//, '').slice(0, 60)}</div>
                              ))
                              : <Dash />}
                          </td>
                          <td className="dim">{when(r.last) || <Dash />}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <p className="cvnote">
                  A dash under Opens beside a number under Clicks means they read it with
                  pictures switched off, which Gmail and Outlook do by default. The click is
                  the proof.
                </p>
              </>
            );
          })() : null}

          {metric === 'bounced' && bounced.length ? (
            <p className="cvnote">
              A bounce names the address inside the notice, not the address it came from — which is
              why these read as your recipient rather than as the mail system. Fix or remove them
              in Marketing → Contacts; every bounce counts against your sending domain.
            </p>
          ) : null}
        </section>
      </div>
    </Sheet>
  );
}
